<template>
  <form @submit.prevent="handleSubmit" class="product-form pb-60">
    <div class="row">
      <div class="col-xxl-9 col-xl-8 col-lg-8 pe-xxl-6 mb-md-25">
        
        <!-- General Information Card -->
        <div class="card border-0 shadow-sm rounded-3 mb-4">
          <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
            <h6 class="fw-semibold mb-0">{{ $t("product_page.general_information") }}</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_name") }} <span class="text-danger">*</span></label>
                  <input type="text" class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2" :placeholder="$t('product_page.enter_product_name')" v-model="form.name" required />
                  <span v-if="duplicateName" class="text-red fs-12 d-block mt-1">{{ $t("product_page.duplicate_name_detected") }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.category") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="form.category_id">
                    <option :value="undefined">{{ $t("product_page.choose_category") }}</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.brand") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="form.brand_id">
                    <option :value="undefined">{{ $t("product_page.choose_brand") }}</option>
                    <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_unit") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="form.unit_id">
                    <option :value="undefined">{{ $t("product_page.choose_product_unit") }}</option>
                    <option v-for="u in units" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.public_emenu") }}</label>
                  <label class="emenu-control" for="showOnEmenu">
                    <input
                      class="emenu-control__input"
                      type="checkbox"
                      id="showOnEmenu"
                      v-model="form.show_on_emenu"
                    />
                    <span class="emenu-control__switch" aria-hidden="true"></span>
                    <span class="emenu-control__content">
                      <span class="emenu-control__title">
                        {{ $t("product_page.show_on_public_emenu") }}
                      </span>
                      <span class="emenu-control__status" :class="{ 'is-enabled': form.show_on_emenu }">
                        {{ form.show_on_emenu ? $t("product_page.enabled") : $t("product_page.disabled") }}
                      </span>
                    </span>
                  </label>
                  <p class="fs-12 text-muted mb-0 mt-2">
                    {{ $t("product_page.public_emenu_help") }}
                  </p>
                  <!--
                    Keep text outside of the switch so global form-control styles cannot
                    stretch the native checkbox into the oversized control shown before.
                  -->
                  <span class="visually-hidden">
                      {{ form.show_on_emenu ? $t("product_page.enabled") : $t("product_page.disabled") }}
                  </span>
                </div>
              </div>
              <!-- <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">Preparation Option</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="form.sugar_id">
                    <option :value="undefined">Choose Preparation Option</option>
                    <option v-for="s in sugars" :key="s.id" :value="s.id">{{ s.name }} ({{ s.code }})</option>
                  </select>
                </div>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Pricing & Inventory Card -->
        <div class="card border-0 shadow-sm rounded-3 mb-4">
          <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
            <h6 class="fw-semibold mb-0">{{ $t("product_page.pricing_inventory") }}</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_type") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="form.type">
                    <option :value="1">{{ $t("product_page.standard_stockable") }}</option>
                    <option :value="2">{{ $t("product_page.service_non_stockable") }}</option>
                  </select>
                </div>
              </div>
              
              <div class="col-lg-6" v-if="form.type === 1">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_cost") }} <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    inputmode="decimal"
                    class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2"
                    placeholder="0.00"
                    v-model="form.cost"
                    :required="form.type === 1"
                    @input="handleDecimalFieldInput('cost')"
                    @blur="formatDecimalField('cost')"
                  />
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_price") }} <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    inputmode="decimal"
                    class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2"
                    placeholder="0.00"
                    v-model="form.price"
                    required
                    @input="handleDecimalFieldInput('price')"
                    @blur="formatDecimalField('price')"
                  />
                </div>
              </div>

              <div class="col-lg-6" v-if="form.type === 1">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_quantity") }} <span class="text-danger">*</span></label>
                  <input type="number" class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2" placeholder="0" v-model.number="form.qty" required />
                </div>
              </div>
              <div class="col-lg-6" v-if="form.type === 1">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.stock_alert") }}</label>
                  <input type="number" class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2" placeholder="0" />
                </div>
              </div>

              <div class="col-lg-6">
                <div class="form-group mb-25 position-relative">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.order_tax") }} <span class="text-muted fs-12">(%)</span></label>
                  <input type="number" class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2 pe-4" placeholder="0" />
                  <span class="position-absolute end-0 top-50 translate-middle-y mt-2 pe-3 text-muted" style="margin-top: 5px;">%</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.tax_type") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2">
                    <option value="1">{{ $t("product_page.inclusive") }}</option>
                    <option value="2">{{ $t("product_page.exclusive") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Barcode Identification Card -->
        <div class="card border-0 shadow-sm rounded-3 mb-4">
          <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
            <h6 class="fw-semibold mb-0">{{ $t("product_page.barcode_identification") }}</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.code_type") }}</label>
                  <div class="d-flex align-items-center gap-4 p-2 bg_ash rounded-1">
                    <div class="form-check checkbox style-two m-0 d-flex align-items-center gap-2">
                      <input class="form-check-input m-0 flex-shrink-0" style="width: 1rem; height: 1rem;" type="radio" v-model="codeType" value="barcode" id="typeBarcode">
                      <label class="form-check-label fs-14 mb-0" for="typeBarcode">{{ $t("product_page.barcode") }}</label>
                    </div>
                    <div class="form-check checkbox style-two m-0 d-flex align-items-center gap-2">
                      <input class="form-check-input m-0 flex-shrink-0" style="width: 1rem; height: 1rem;" type="radio" v-model="codeType" value="qrcode" id="typeQRcode">
                      <label class="form-check-label fs-14 mb-0" for="typeQRcode">{{ $t("product_page.qr_code") }}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="codeType === 'barcode'" class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.symbology") }}</label>
                  <select class="w-100 bg_ash border-0 rounded-1 fs-14 text-optional p-2" v-model="barcodeFormat">
                    <option value="CODE128">CODE128</option>
                    <option value="CODE39">CODE39</option>
                    <option value="EAN13">EAN13</option>
                    <option value="EAN8">EAN8</option>
                    <option value="UPC">UPC</option>
                    <option value="ITF14">ITF14</option>
                  </select>
                </div>
              </div>

              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.product_code") }} <span class="text-danger">*</span></label>
                  <input type="text" class="w-100 d-block shadow-none fs-14 bg_ash rounded-1 text-title border-0 p-2" :placeholder="$t('product_page.scan_or_enter_code')" v-model="form.code" required />
                  <span class="text-muted d-block w-100 fs-12 mt-1">
                    {{ $t("product_page.scan_barcode_helper") }}
                  </span>
                </div>
              </div>

              <div class="col-lg-6">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.preview") }}</label>
                  <div class="barcode-preview-container p-3 bg_ash rounded-1 d-flex flex-column align-items-center">
                    <div class="bg-white p-2 rounded-1 w-100 d-flex justify-content-center mb-2" style="min-height: 80px;">
                      <BarcodeGenerator v-if="codeType === 'barcode' && form.code" :value="form.code" :format="barcodeFormat" :height="40" :width="1.5" />
                      <QRCodeGenerator v-if="codeType === 'qrcode' && form.code" :value="form.code" :size="60" />
                      <span v-if="!form.code" class="text-muted fs-12 mt-4">{{ $t("product_page.code_preview") }}</span>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm w-100 border-1" @click="generateBarcode">{{ $t("product_page.generate_random_code") }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Variants & Sizes Card -->
        <div class="card border-0 shadow-sm rounded-3 mb-4">
          <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
            <h6 class="fw-semibold mb-0">{{ $t("product_page.variants_sizes") }}</h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group mb-25">
                  <label class="d-block fs-14 text-black mb-2">{{ $t("product_page.add_product_size_variants") }}</label>
                  <div class="d-flex gap-2 mb-3">
                    <select class="bg_ash border-0 rounded-1 fs-14 text-optional p-2 flex-grow-1" v-model="selectedSizeId">
                      <option :value="undefined">{{ $t("product_page.choose_size") }}</option>
                      <option v-for="s in sizes" :key="s.id" :value="s.id">{{ s.name }} ({{ s.code }})</option>
                    </select>
                    <input
                      type="text"
                      inputmode="decimal"
                      class="bg_ash border-0 rounded-1 fs-14 text-title p-2 w-25"
                      :placeholder="$t('product_page.variant_price')"
                      v-model="selectedSizePrice"
                      @input="handleSelectedSizePriceInput"
                      @blur="formatSelectedSizePrice"
                    />
                    <button type="button" class="btn btn-primary px-4" @click="addSizePrice">{{ $t("product_page.add") }}</button>
                  </div>
                  
                  <div v-if="form.product_sizes && form.product_sizes.length > 0">
                    <ul class="list-group border-0 gap-2">
                      <li v-for="(ps, index) in form.product_sizes" :key="index" class="list-group-item d-flex justify-content-between align-items-center bg_ash border-0 rounded-1 px-3 py-2 fs-14 text-title">
                        <div>
                          <span class="fw-semibold me-2">{{ getSizeName(ps.size_id) }}</span>
                          <span class="text-success fw-bold">${{ ps.price }}</span>
                        </div>
                        <button type="button" class="btn btn-sm btn-light text-danger bg-white border-0 py-1" @click="removeSizePrice(index)">
                          <img src="../../../assets/img/icons/close.svg" :alt="$t('product_page.remove')" style="width: 12px; opacity: 0.6;" />
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notes Card -->
        <div class="card border-0 shadow-sm rounded-3 mb-4">
          <div class="card-body">
            <div class="form-group mb-0">
              <label class="d-block fs-14 fw-semibold text-black mb-2">{{ $t("product_page.internal_notes") }}</label>
              <textarea
                cols="30"
                rows="4"
                :placeholder="$t('product_page.add_note_about_product')"
                class="d-block w-100 bg_ash border-0 rounded-1 resize-none fs-14 text-title p-3"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="d-flex gap-2 mb-4 mb-lg-0">
          <button
            class="btn style-one product-submit-btn transition border-0 fw-medium text-white rounded-1 fs-md-15 fs-lg-16 px-5 py-2"
            type="submit"
          >
             <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEdit ? $t("product_page.update_product") : $t("product_page.submit_product") }}
          </button>
          <router-link to="/product-list" class="btn btn-light rounded-1 px-4 py-2 text-dark">
            {{ $t("common.cancel") }}
          </router-link>
        </div>
      </div>

      <!-- Right Column: Images -->
      <div class="col-xxl-3 col-xl-4 col-lg-4 mt-4 mt-lg-0">
        <div class="card border-0 shadow-sm rounded-3 position-sticky" style="top: 20px;">
          <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
            <h6 class="fw-semibold mb-0">{{ $t("product_page.product_images") }}</h6>
          </div>
          <div class="card-body">
            <AddImages 
              :key="addImagesKey"
              :existingImages="existingAttachments.map(a => a.url)"
              @update:files="handleFilesUpdate"
              @remove:existing="handleRemoveExisting"
            />
            <p class="fs-12 text-muted mt-3 mb-0 text-center">
              {{ $t("product_page.recommended_image_size") }} <br>
              {{ $t("product_page.max_file_size") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import AddImages from "./AddImages.vue";
import Swal from "sweetalert2";
import stateStore from "@/utils/store";
import productService from "@/services/product";
import apiClient from "@/services/api";
import { buildStorageAssetUrl } from "@/utils/settings";
import brandService from "@/services/brand";
import unitService from "@/services/unit";
import { sizeService, Size } from "@/services/size";
import { sugarService, Sugar } from "@/services/sugar";
import BarcodeGenerator from "../../Common/BarcodeGenerator.vue";
import QRCodeGenerator from "../../Common/QRCodeGenerator.vue";

export default defineComponent({
  name: "CreateProducts",
  components: {
    AddImages,
    BarcodeGenerator,
    QRCodeGenerator,
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const isEdit = ref(false);
    type Category = { id: number; name: string; code?: string; status?: boolean; description?: string };
    const categories = ref<Category[]>([]);
    type Brand = { id?: number; name: string; code: string };
    const brands = ref<Brand[]>([]);
    type Unit = { id?: number; name: string; code: string };
    const units = ref<Unit[]>([]);
    const sizes = ref<Size[]>([]);
    const sugars = ref<Sugar[]>([]);
    const form = ref({
      id: null as number | null,
      code: "",
      name: "",
      cost: "0.00",
      price: "0.00",
      qty: 0,
      type: 1,
      show_on_emenu: true,
      category_id: undefined as number | undefined,
      brand_id: undefined as number | undefined,
      unit_id: undefined as number | undefined,
      size_id: undefined as number | undefined,
      sugar_id: undefined as number | undefined,
      product_sizes: [] as { size_id: number; price: string }[],
    });

    const selectedSizeId = ref<number | undefined>(undefined);
    const selectedSizePrice = ref("");

    const sanitizeDecimalInput = (value: string | number | null | undefined, maxDecimals = 2) => {
      const raw = String(value ?? "").replace(/,/g, ".").replace(/[^\d.]/g, "");
      if (!raw) return "";

      const firstDotIndex = raw.indexOf(".");
      if (firstDotIndex === -1) {
        return raw;
      }

      const integerPart = raw.slice(0, firstDotIndex);
      const decimalPart = raw
        .slice(firstDotIndex + 1)
        .replace(/\./g, "")
        .slice(0, maxDecimals);

      return `${integerPart}.${decimalPart}`;
    };

    const formatDecimalInput = (value: string | number | null | undefined, maxDecimals = 2) => {
      const sanitized = sanitizeDecimalInput(value, maxDecimals);

      if (!sanitized || sanitized === ".") {
        return "";
      }

      const parsed = Number(sanitized);
      return Number.isFinite(parsed) ? parsed.toFixed(maxDecimals) : "";
    };

    const handleDecimalFieldInput = (field: "cost" | "price") => {
      form.value[field] = sanitizeDecimalInput(form.value[field]);
    };

    const formatDecimalField = (field: "cost" | "price") => {
      const formatted = formatDecimalInput(form.value[field]);
      if (formatted) {
        form.value[field] = formatted;
      }
    };

    const handleSelectedSizePriceInput = () => {
      selectedSizePrice.value = sanitizeDecimalInput(selectedSizePrice.value);
    };

    const formatSelectedSizePrice = () => {
      const formatted = formatDecimalInput(selectedSizePrice.value);
      if (formatted) {
        selectedSizePrice.value = formatted;
      }
    };

    const addSizePrice = () => {
      const formattedSizePrice = formatDecimalInput(selectedSizePrice.value);

      if (selectedSizeId.value && formattedSizePrice) {
        const exists = form.value.product_sizes.some(
          (ps) => Number(ps.size_id) === Number(selectedSizeId.value)
        );

        if (exists) {
          Swal.fire({
            icon: "warning",
            title: t("product_page.size_already_added"),
            text: t("product_page.size_already_in_list"),
          });
          return;
        }

        form.value.product_sizes.push({
          size_id: Number(selectedSizeId.value),
          price: formattedSizePrice,
        });
        selectedSizeId.value = undefined;
        selectedSizePrice.value = "";
      }
    };

    const removeSizePrice = (index: number) => {
      form.value.product_sizes.splice(index, 1);
    };

    const getSizeName = (id: number) => {
      const numericId = Number(id);
      const s = sizes.value.find((x) => Number(x.id) === numericId);
      return s ? `${s.name} (${s.code})` : t("product_page.size_with_id", { id: numericId });
    };

    const loading = ref(false);
    const duplicateName = ref(false);
    const addImagesKey = ref(0);

    const fetchCategories = async () => {
      try {
        const res = await apiClient.get("/categories");
        categories.value = res.data;
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    const fetchBrands = async () => {
      try {
        brands.value = await brandService.getBrands();
      } catch (error) {
        console.error("Failed to fetch brands:", error);
      }
    };
    const fetchUnits = async () => {
      try {
        units.value = await unitService.getUnits();
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };
    const fetchSizes = async () => {
      try {
        sizes.value = await sizeService.getSizes();
      } catch (error) {
        console.error("Failed to fetch sizes:", error);
      }
    };
    const fetchSugars = async () => {
      try {
        sugars.value = await sugarService.getSugars();
      } catch (error) {
        console.error("Failed to fetch preparation options:", error);
      }
    };

    const existingAttachments = ref<{ id: number; url: string }[]>([]);
    const deletedAttachmentIds = ref<number[]>([]);

    const loadProduct = async (id: number) => {
      loading.value = true;
      try {
        const p = await productService.getProduct(id);
        isEdit.value = true;
        form.value = {
          id: p.id ?? null,
          code: p.code,
          name: p.name,
          cost: formatDecimalInput(p.cost) || "0.00",
          price: formatDecimalInput(p.price) || "0.00",
          qty: p.qty ?? 0,
          type: p.type ?? 1,
          show_on_emenu: p.show_on_emenu === undefined ? true : Boolean(p.show_on_emenu),
          category_id: p.category_id ?? p.category?.id,
          brand_id: p.brand_id ?? p.brand?.id,
          unit_id: p.unit_id ?? p.unit?.id,
          size_id: p.size_id ?? p.size?.id,
          sugar_id: p.sugar_id ?? p.sugar?.id,
          product_sizes: (p.product_sizes || []).map((ps) => ({
            size_id: Number(ps.size_id),
            price: formatDecimalInput(ps.price) || "0.00",
          })),
        };

        if (p.attachments) {
          existingAttachments.value = p.attachments
            .filter((a) => a.id && (a.file_path || a.path))
            .map((a) => ({
              id: a.id!,
              url: buildStorageAssetUrl(a.file_path || a.path),
            }));
        }
      } catch (error) {
        console.error("Failed to load product:", error);
      } finally {
        loading.value = false;
      }
    };

    const handleRemoveExisting = (index: number) => {
      const att = existingAttachments.value[index];
      if (att.id) {
        deletedAttachmentIds.value.push(att.id);
      }
      existingAttachments.value.splice(index, 1);
    };

    const randomDigits = (len: number) => {
      let s = "";
      for (let i = 0; i < len; i++) s += Math.floor(Math.random() * 10).toString();
      return s;
    };

    const randomAlnum = (len: number) => {
      const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
      let s = "";
      for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
      return s;
    };

    const barcodeFormat = ref<string>("CODE128");
    const codeType = ref<"barcode" | "qrcode">("barcode");

    const generateBarcode = () => {
      let value = form.value.code.trim();
      if (!value) {
        const prefix = stateStore.referencePrefixes.products || "SKU-";
        switch (barcodeFormat.value) {
          case "EAN13":
            value = randomDigits(12);
            break;
          case "EAN8":
            value = randomDigits(7);
            break;
          case "UPC":
            value = randomDigits(11);
            break;
          case "CODE39":
          case "CODE128":
          default:
            value = `${prefix}${randomAlnum(8)}`;
            break;
          case "ITF14":
            value = randomDigits(13);
            break;
        }
        form.value.code = value;
      }
    };

    onMounted(() => {
      fetchCategories();
      fetchBrands();
      fetchUnits();
      fetchSizes();
      fetchSugars();
      const idParam = route.query.id;
      if (typeof idParam === "string") {
        const id = parseInt(idParam, 10);
        if (!isNaN(id)) {
          loadProduct(id);
        }
      }
    });

    const selectedFiles = ref<File[]>([]);
    const handleFilesUpdate = (files: File[]) => {
      selectedFiles.value = files;
    };

    const makeSlug = (name: string, code: string): string => {
      const n = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      const c = String(code).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return `${n}-${c}`.replace(/-+/g, "-");
    };

    const handleSubmit = async () => {
      if (duplicateName.value) {
        Swal.fire({
          icon: 'error',
          title: t("product_page.duplicate_name_title"),
          text: t("product_page.duplicate_name_message"),
        });
        return;
      }
      loading.value = true;
      try {
        const formattedPrice = formatDecimalInput(form.value.price);
        const formattedCost = form.value.type === 1
          ? formatDecimalInput(form.value.cost)
          : "0.00";

        if (!formattedPrice) {
          Swal.fire({
            icon: "error",
            title: t("product_page.invalid_price"),
            text: t("product_page.invalid_price_message"),
          });
          return;
        }

        if (form.value.type === 1 && !formattedCost) {
          Swal.fire({
            icon: "error",
            title: t("product_page.invalid_cost"),
            text: t("product_page.invalid_cost_message"),
          });
          return;
        }

        const formData = new FormData();
        formData.append("code", form.value.code);
        formData.append("name", form.value.name);
        formData.append("cost", formattedCost);
        formData.append("price", formattedPrice);
        formData.append("qty", String(form.value.qty));
        formData.append("type", String(form.value.type));
        formData.append("show_on_emenu", form.value.show_on_emenu ? "1" : "0");
        if (form.value.category_id) formData.append("category_id", String(form.value.category_id));
        if (form.value.brand_id) formData.append("brand_id", String(form.value.brand_id));
        if (form.value.unit_id) formData.append("unit_id", String(form.value.unit_id));
        if (form.value.size_id) formData.append("size_id", String(form.value.size_id));
        if (form.value.sugar_id) formData.append("sugar_id", String(form.value.sugar_id));
        
        const slug = makeSlug(form.value.name, form.value.code);
        formData.append("slug", slug);

        if (form.value.product_sizes && form.value.product_sizes.length > 0) {
          form.value.product_sizes.forEach((ps, index) => {
            formData.append(`product_sizes[${index}][size_id]`, String(ps.size_id));
            formData.append(
              `product_sizes[${index}][price]`,
              formatDecimalInput(ps.price) || "0.00"
            );
          });
        }

        selectedFiles.value.forEach((file) => {
          formData.append("images[]", file);
        });

        if (deletedAttachmentIds.value.length > 0) {
          deletedAttachmentIds.value.forEach((id) => {
            formData.append("deleted_image_ids[]", String(id));
          });
        }

        if (isEdit.value && form.value.id) {
          // Laravel requires _method=PUT for multipart/form-data updates
          formData.append("_method", "PUT");
          await productService.updateProduct(form.value.id, formData);
          stateStore.triggerRefresh();
          
          Swal.fire({
            icon: 'success',
            title: t("product_page.save_success_title"),
            text: t("product_page.product_updated_successfully"),
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            router.push('/product-list');
          });
          
        } else {
          await productService.createProduct(formData);
          stateStore.triggerRefresh();
          
          Swal.fire({
            icon: 'success',
            title: t("product_page.save_success_title"),
            text: t("product_page.product_created_successfully"),
            timer: 1500,
            showConfirmButton: false
          }).then(() => {
            router.push('/product-list');
          });
          
          form.value = {
            id: null,
            code: "",
            name: "",
            cost: "0.00",
            price: "0.00",
            qty: 0,
            type: 1,
            show_on_emenu: true,
            category_id: undefined,
            brand_id: undefined,
            unit_id: undefined,
            size_id: undefined,
            sugar_id: undefined,
            product_sizes: [],
          };
          selectedFiles.value = [];
          addImagesKey.value++;
        }
      } catch (error: any) {
        console.error("Save product error:", error);
        let msg = t("product_page.save_failed");
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          msg = Object.values(errors).flat().join("\n");
        } else if (error.response?.data?.message) {
          msg = error.response.data.message;
        }
        Swal.fire({
          icon: 'error',
          title: t("product_page.error_title"),
          text: msg
        });
      } finally {
        loading.value = false;
      }
    };
    watch(
      () => form.value.name,
      async (val) => {
        const name = (val || "").trim().toLowerCase();
        if (!name) {
          duplicateName.value = false;
          return;
        }
        try {
          const products = await productService.getProducts();
          duplicateName.value = products.some(
            (p) => (p.name || "").toLowerCase() === name && p.id !== form.value.id
          );
        } catch {
          duplicateName.value = false;
        }
      }
    );

    return {
      isEdit,
      form,
      loading,
      handleSubmit,
      categories,
      brands,
      units,
      sizes,
      sugars,
      barcodeFormat,
      codeType,
      generateBarcode,
      duplicateName,
      selectedSizeId,
      selectedSizePrice,
      addSizePrice,
      removeSizePrice,
      getSizeName,
      handleDecimalFieldInput,
      formatDecimalField,
      handleSelectedSizePriceInput,
      formatSelectedSizePrice,
      handleFilesUpdate,
      existingAttachments,
      handleRemoveExisting,
      addImagesKey,
      t,
    };
  },
});
</script>

<style lang="scss" scoped>
.product-form {
  .product-submit-btn {
    &:before {
      background: linear-gradient(132deg, #4338ca 4.27%, #4f46e5 100%);
    }

    &:hover,
    &:focus-visible {
      color: #fff;
      border-color: transparent;
      box-shadow: 0 10px 24px rgba(79, 70, 229, 0.2);
    }
  }
}

.emenu-control {
  align-items: center;
  background: #f4f7fb;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  min-height: 58px;
  padding: 10px 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  width: 100%;

  &:hover {
    background: #f8fafc;
    border-color: #cfd8e6;
  }
}

.emenu-control__input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.emenu-control__switch {
  background: #cbd5e1;
  border-radius: 999px;
  flex: 0 0 auto;
  height: 28px;
  position: relative;
  transition: background 0.2s ease;
  width: 52px;

  &::after {
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2);
    content: "";
    height: 22px;
    left: 3px;
    position: absolute;
    top: 3px;
    transition: transform 0.2s ease;
    width: 22px;
  }
}

.emenu-control__input:checked + .emenu-control__switch {
  background: #16a34a;

  &::after {
    transform: translateX(24px);
  }
}

.emenu-control__input:focus-visible + .emenu-control__switch {
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.16);
}

.emenu-control__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.emenu-control__title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
}

.emenu-control__status {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;

  &.is-enabled {
    color: #15803d;
  }
}
</style>
